import { supabase } from "@/integrations/supabase/client";
import { getCategoryPath } from "@/lib/categoryRedirects";

interface LinkTarget {
  keyword: string;
  url: string;
  type: 'product' | 'category' | 'brand' | 'listicle';
}


// Cache for link targets to avoid repeated DB calls
let linkTargetsCache: LinkTarget[] | null = null;
let cacheTimestamp: number = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export async function fetchLinkTargets(): Promise<LinkTarget[]> {
  const now = Date.now();
  
  // Return cached data if still valid
  if (linkTargetsCache && (now - cacheTimestamp) < CACHE_DURATION) {
    return linkTargetsCache;
  }

  const targets: LinkTarget[] = [];

  // Fetch products from all product tables, link SKU -> product page
  const productSources: { table: string; route: string }[] = [
    { table: 'lawn_mowers', route: '/lawn-mowers' },
    { table: 'generators', route: '/generators' },
    { table: 'chainsaws', route: '/chainsaws' },
    { table: 'pressure_washers', route: '/pressure-washers' },
    { table: 'impact_drivers', route: '/impact-drivers' },
  ];

  for (const src of productSources) {
    const { data } = await supabase
      .from(src.table as any)
      .select('sku, slug')
      .eq('status', 'published')
      .not('sku', 'is', null);
    if (data) {
      for (const p of data as any[]) {
        if (p.sku && String(p.sku).trim().length >= 3) {
          targets.push({
            keyword: String(p.sku).trim(),
            url: `${src.route}/${p.slug}`,
            type: 'product',
          });
        }
      }
    }
  }

  // Generic products table (uses product_type as route prefix)
  const { data: genericProducts } = await supabase
    .from('products')
    .select('sku, slug, product_type')
    .eq('status', 'published')
    .not('sku', 'is', null);
  if (genericProducts) {
    for (const p of genericProducts as any[]) {
      if (p.sku && String(p.sku).trim().length >= 3 && p.product_type) {
        targets.push({
          keyword: String(p.sku).trim(),
          url: `/${p.product_type}/${p.slug}`,
          type: 'product',
        });
      }
    }
  }

  // Fetch categories
  const { data: categories } = await supabase
    .from('categories')
    .select('name, slug');

  const MONTHS = /^(january|february|march|april|may|june|july|august|september|october|november|december)$/i;
  if (categories) {
    for (const category of categories) {
      if (MONTHS.test(category.name.trim())) continue;
      const url = getCategoryPath(category.name) || getCategoryPath(category.slug);
      if (!url) continue;
      targets.push({
        keyword: category.name,
        url,
        type: 'category'
      });
    }
  }

  // ---- Hyundai brand targets ----
  // Brand hub + sub-pillar + 2026 listicle
  const hyundaiBrandTargets: LinkTarget[] = [
    { keyword: 'Best Hyundai Lawn Mowers UK', url: '/best/best-hyundai-lawn-mowers-uk', type: 'listicle' },
    { keyword: 'Hyundai lawn mower reviews', url: '/brand/hyundai/lawnmowers', type: 'brand' },
    { keyword: 'Hyundai lawnmower reviews', url: '/brand/hyundai/lawnmowers', type: 'brand' },
    { keyword: 'Hyundai lawn mowers', url: '/brand/hyundai/lawnmowers', type: 'brand' },
    { keyword: 'Hyundai lawnmowers', url: '/brand/hyundai/lawnmowers', type: 'brand' },
    { keyword: 'Hyundai lawn mower', url: '/brand/hyundai/lawnmowers', type: 'brand' },
    { keyword: 'Hyundai lawnmower', url: '/brand/hyundai/lawnmowers', type: 'brand' },
    { keyword: 'Hyundai mowers', url: '/brand/hyundai/lawnmowers', type: 'brand' },
    { keyword: 'Hyundai mower', url: '/brand/hyundai/lawnmowers', type: 'brand' },
    { keyword: 'Hyundai power products', url: '/brand/hyundai', type: 'brand' },
    { keyword: 'Hyundai Power Equipment', url: '/brand/hyundai', type: 'brand' },
  ];
  targets.push(...hyundaiBrandTargets);

  // ---- High-value "money hub" buyer guides ----
  // Long, specific keyphrases first (autoLinker sorts by length) so they win over generic matches.
  const moneyHubTargets: LinkTarget[] = [
    // Lawn mowers
    { keyword: 'best cordless lawn mowers UK', url: '/articles/best-cordless-lawn-mowers-uk-2026', type: 'listicle' },
    { keyword: 'best cordless lawn mower', url: '/articles/best-cordless-lawn-mowers-uk-2026', type: 'listicle' },
    { keyword: 'best lawn mowers UK', url: '/best-lawn-mowers-uk', type: 'listicle' },
    { keyword: 'best lawn mower', url: '/best-lawn-mowers-uk', type: 'listicle' },
    { keyword: 'lawn mower reviews', url: '/best-lawn-mowers-uk', type: 'listicle' },
    // Generators
    { keyword: 'best diesel generators UK', url: '/best/best-diesel-generators-uk', type: 'listicle' },
    { keyword: 'best generators UK', url: '/best-generators-uk', type: 'listicle' },
    { keyword: 'best generator', url: '/best-generators-uk', type: 'listicle' },
    // Chainsaws
    { keyword: 'best chainsaws UK', url: '/best-chainsaws-uk', type: 'listicle' },
    { keyword: 'best chainsaw', url: '/best-chainsaws-uk', type: 'listicle' },
    // Pressure washers
    { keyword: 'best pressure washers UK', url: '/best-pressure-washers-uk', type: 'listicle' },
    { keyword: 'best pressure washer', url: '/best-pressure-washers-uk', type: 'listicle' },
    // Leaf blowers
    { keyword: 'best leaf blowers UK', url: '/guides/best-leaf-blowers-uk', type: 'listicle' },
    { keyword: 'best leaf blower', url: '/guides/best-leaf-blowers-uk', type: 'listicle' },
    { keyword: 'leaf blower guide', url: '/leaf-blower-guide', type: 'listicle' },
    // Hedge trimmers (recently beefed up hub)
    { keyword: 'best hedge trimmers UK', url: '/best-hedge-trimmers-uk', type: 'listicle' },
    { keyword: 'best hedge trimmer', url: '/best-hedge-trimmers-uk', type: 'listicle' },
    { keyword: 'hedge trimmer reviews', url: '/hedge-trimmers', type: 'category' },
    // Other buyer guide hubs
    { keyword: 'best scarifiers UK', url: '/best-scarifiers-uk', type: 'listicle' },
    { keyword: 'best log splitters UK', url: '/best-log-splitters-uk', type: 'listicle' },
    { keyword: 'best shredders UK', url: '/best-shredders-uk', type: 'listicle' },
    { keyword: 'best grass trimmers UK', url: '/best-grass-trimmers-brushcutters-uk', type: 'listicle' },
    { keyword: 'best strimmer', url: '/best-grass-trimmers-brushcutters-uk', type: 'listicle' },
    { keyword: 'best tillers UK', url: '/best-tillers-rotavators-uk', type: 'listicle' },
    { keyword: 'best rotavator', url: '/best-tillers-rotavators-uk', type: 'listicle' },
    { keyword: 'best water pumps UK', url: '/best-water-pumps-uk', type: 'listicle' },
    { keyword: 'best air compressors UK', url: '/best-air-compressors-uk', type: 'listicle' },
  ];
  targets.push(...moneyHubTargets);


  // Hyundai HYM* model numbers — dynamically pulled from published mowers
  const { data: hyundaiMowers } = await supabase
    .from('lawn_mowers')
    .select('model, slug')
    .eq('status', 'published')
    .ilike('brand', 'hyundai');
  if (hyundaiMowers) {
    const seenModels = new Set<string>();
    for (const m of hyundaiMowers as any[]) {
      const firstToken = String(m.model || '')
        .split(/[\s—–\-(]/)
        .find(Boolean);
      if (!firstToken) continue;
      const code = firstToken.trim();
      // Only link recognisable Hyundai model codes (HYM…, HY…)
      if (!/^HY[A-Z0-9]{2,}$/i.test(code)) continue;
      const key = code.toUpperCase();
      if (seenModels.has(key)) continue;
      seenModels.add(key);
      // Plain model code
      targets.push({
        keyword: code,
        url: `/lawn-mowers/${m.slug}`,
        type: 'product',
      });
      // "Hyundai HYM510SPE" pattern — higher specificity, sorts first
      targets.push({
        keyword: `Hyundai ${code}`,
        url: `/lawn-mowers/${m.slug}`,
        type: 'product',
      });
    }
  }

  // ---- Plant guides (/plants/:slug) ----
  const { data: plantGuides } = await supabase
    .from('articles')
    .select('title, slug, tags')
    .eq('status', 'published')
    .contains('tags', ['Plant Guide']);

  if (plantGuides) {
    const seenKeywords = new Set<string>();
    const pushGuide = (keyword: string, url: string) => {
      const k = keyword.trim();
      if (k.length < 5) return;
      const lower = k.toLowerCase();
      if (seenKeywords.has(lower)) return;
      seenKeywords.add(lower);
      targets.push({ keyword: k, url, type: 'listicle' });
    };

    for (const g of plantGuides as any[]) {
      const url = `/articles/${g.slug}`;
      const title = String(g.title || '');
      // "How to Grow Hostas: Complete UK Care Guide" -> "how to grow hostas"
      const headline = title.split(/[:|–—]/)[0]?.trim();
      if (headline) pushGuide(headline, url);

      // Plant name from tags (skip the generic ones)
      const tags: string[] = Array.isArray(g.tags) ? g.tags : [];
      for (const tag of tags) {
        const t = String(tag).trim();
        if (!t || /^(plant guide|shrubs|trees|perennials|climbers|bulbs|houseplants|hedging|pest control|vegetables|fruit)$/i.test(t)) continue;
        pushGuide(`how to grow ${t}`, url);
        pushGuide(`growing ${t}`, url);
        pushGuide(t, url);
      }
    }
  }

  // Plant hub
  targets.push({ keyword: 'plant guides', url: '/plants', type: 'category' });
  targets.push({ keyword: 'A-Z of plants', url: '/plants', type: 'category' });

  // Prioritise specific pages (products, guides, brands) over generic categories,
  // then longest keyword first so specific phrases win.
  const typeRank: Record<LinkTarget['type'], number> = {
    product: 0,
    listicle: 1,
    brand: 2,
    category: 3,
  };
  targets.sort(
    (a, b) => typeRank[a.type] - typeRank[b.type] || b.keyword.length - a.keyword.length
  );


  // Update cache
  linkTargetsCache = targets;
  cacheTimestamp = now;

  return targets;
}

/**
 * Auto-links keywords in HTML content to relevant articles and categories.
 * Avoids linking inside existing <a> tags, headings, and code blocks.
 */
export function applyAutoLinks(
  htmlContent: string,
  linkTargets: LinkTarget[],
  currentArticleSlug?: string
): string {
  if (!htmlContent || linkTargets.length === 0) {
    return htmlContent;
  }

  // Track which keywords we've already linked (first occurrence only)
  const linkedKeywords = new Set<string>();
  // One link per destination URL, and a sane sitewide cap to avoid link spam
  const linkedUrls = new Set<string>();
  const MAX_AUTO_LINKS = 12;

  // Process each target
  let result = htmlContent;

  for (const target of linkTargets) {
    if (linkedUrls.size >= MAX_AUTO_LINKS) break;
    if (linkedUrls.has(target.url)) continue;
    // Skip if this is the current article (either URL shape)
    if (
      currentArticleSlug &&
      (target.url === `/articles/${currentArticleSlug}` ||
        target.url === `/plants/${currentArticleSlug}`)
    ) {
      continue;
    }

    // Skip if we've already linked this keyword
    const keywordLower = target.keyword.toLowerCase();
    if (linkedKeywords.has(keywordLower)) {
      continue;
    }

    // Create a regex that matches the keyword as a whole word, case-insensitive
    const escapedKeyword = target.keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    
    // Match keyword that's not inside an HTML tag
    const regex = new RegExp(
      `(?<!<[^>]*)\\b(${escapedKeyword})\\b(?![^<]*>)`,
      'gi'
    );

    // Find first valid match
    let matched = false;
    result = result.replace(regex, (match, group, offset) => {
      // Only link the first occurrence
      if (matched) {
        return match;
      }

      // Check if we're inside a tag we shouldn't link within
      const beforeMatch = result.substring(0, offset);
      
      // Count open tags to determine context
      const openATags = (beforeMatch.match(/<a[^>]*>/gi) || []).length;
      const closeATags = (beforeMatch.match(/<\/a>/gi) || []).length;
      const insideATag = openATags > closeATags;
      
      // Check for other tags we shouldn't modify content in
      const openHeadings = (beforeMatch.match(/<h[1-6][^>]*>/gi) || []).length;
      const closeHeadings = (beforeMatch.match(/<\/h[1-6]>/gi) || []).length;
      const insideHeading = openHeadings > closeHeadings;
      
      const openCode = (beforeMatch.match(/<code[^>]*>/gi) || []).length;
      const closeCode = (beforeMatch.match(/<\/code>/gi) || []).length;
      const insideCode = openCode > closeCode;
      
      const openPre = (beforeMatch.match(/<pre[^>]*>/gi) || []).length;
      const closePre = (beforeMatch.match(/<\/pre>/gi) || []).length;
      const insidePre = openPre > closePre;

      // If inside a protected context, don't link
      if (insideATag || insideHeading || insideCode || insidePre) {
        return match;
      }

      // Mark as matched and linked
      matched = true;
      linkedKeywords.add(keywordLower);
      linkedUrls.add(target.url);
      // No title attribute: some crawlers glued `title="Read more about X"`
      // onto the href, creating junk URLs. Also escape the href defensively.
      const safeUrl = String(target.url).replace(/["<>\s]/g, "");
      return `<a href="${safeUrl}" class="auto-link">${match}</a>`;
    });
  }

  return result;
}

/**
 * Hook to use auto-linking in components
 */
export async function autoLinkContent(
  content: string,
  currentArticleSlug?: string
): Promise<string> {
  const targets = await fetchLinkTargets();
  return applyAutoLinks(content, targets, currentArticleSlug);
}
